import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GridFilterModel, GridRowId, GridSortModel } from "@mui/x-data-grid-pro";
import {
    createDefaultBasicSearchParams,
    InventoryAdvancedSearchParams,
    InventoryList,
    InventoryBasicSearchParams,
    SearchTab, InventoryFileSearchParams,
} from "../../model/Inventory";
import { GridColumnDef, GridColumnVisibilityMap } from "../../components/grids/grid";

export interface InventoryBasicSearch {
    active: boolean;
    params: InventoryBasicSearchParams;
}

export interface InventoryAdvancedSearch {
    active: boolean;
    params: InventoryAdvancedSearchParams;
}

export interface InventoryFileSearch {
    active: boolean;
    params: InventoryFileSearchParams;
}

export interface InventoryState {
    inventoryData: InventoryList;
    inventoryDataRetrievedTS: string;
    currentPage: number;
    pageSize: number;
    filterModel: GridFilterModel;
    selectionModel: Array<GridRowId>;
    basicSearch: InventoryBasicSearch;
    basicSearchCsv: InventoryBasicSearch;
    advancedSearch: InventoryAdvancedSearch;
    fileSearch: InventoryFileSearch;
    sortModel: GridSortModel;
    searchType: SearchTab;
    columnDef: GridColumnDef[];
    columnVisibility: GridColumnVisibilityMap;
}

const initialState: InventoryState = {
    inventoryData: [],
    inventoryDataRetrievedTS: "",
    currentPage: 0,
    pageSize: 100,
    filterModel: {items: []},
    selectionModel: [],
    basicSearch: {
        active: false,
        params: createDefaultBasicSearchParams(),
    },
    basicSearchCsv: {
        active: false,
        params: createDefaultBasicSearchParams(),
    },
    advancedSearch: {
        active: false,
        params: {
                code: '',
                parameters: []
        },
    },
    fileSearch: {
        active: false,
        params: {
            file: "",
        }
    },
    sortModel: [],
    searchType: SearchTab.basic,
    columnDef: [
        {field: 'id'},
        {field: 'view'},
        {field: 'cartStatus'},
        {field: 'scanId'},
        {field: 'barcode'},
        {field: 'altCode'},
        {field: 'type'},
        {field: 'status'},
        {field: 'statusDate'},
        {field: 'addDate'},
        {field: 'destroyDate'},
        {field: 'accountLevel1'},
        {field: 'accountLevel2'},
        {field: 'accountLevel3'},
        {field: 'contentsRangeStart'},
        {field: 'contentsRangeEnd'},
        {field: 'contentsStartDate'},
        {field: 'contentsEndDate'},
        {field: 'shortDescription'},
        {field: 'longDescription'},
        {field: 'contents'},
        {field: 'setName'},
        {field: 'recordSeries'},
        {field: 'containerBarcode'},
        {field: 'containerAltCode'},
        {field: 'customField1'},
        {field: 'customField2'},
        {field: 'customField3'},
        {field: 'customField4'},
        {field: 'customDate'},
        {field: 'permanent'},
    ],
    columnVisibility: {
        id: false,
        view: false,
        cartStatus: false,
    }
}

export const inventorySlice = createSlice({
    name: 'inventory',
    initialState,
    reducers: {
        setInventoryData: (state, action: PayloadAction<InventoryList>) => {
            state.inventoryData = action.payload;
        },

        setInventoryDataRetrievedTS: (state, action: PayloadAction<string>) => {
            state.inventoryDataRetrievedTS = action.payload;
        },

        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },

        setPageSize: (state, action: PayloadAction<number>) => {
            state.pageSize = action.payload;
        },

        setSelectionModel: (state, action: PayloadAction<Array<GridRowId>>) => {
            state.selectionModel = action.payload;
        },

        setFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
            state.filterModel = action.payload;
        },

        setSortModel: (state, action: PayloadAction<GridSortModel>) => {
            state.sortModel = action.payload;
        },

        setBasicSearchParams: (state, action: PayloadAction<InventoryBasicSearchParams>) => {
            state.currentPage = 0;
            state.basicSearch.active = true;
            state.basicSearch.params = action.payload;
            state.advancedSearch.active = false;
            state.fileSearch.active = false;
        },

        setBasicSearchParamsCsv: (state, action: PayloadAction<InventoryBasicSearchParams>) => {
            state.currentPage = 0;
            state.basicSearch.active = true;
            state.basicSearchCsv.params = action.payload;
            state.advancedSearch.active = false;
            state.fileSearch.active = false;
        },

        setAdvancedSearchParams: (state, action: PayloadAction<InventoryAdvancedSearchParams>) => {
            state.currentPage = 0;
            state.advancedSearch.active = true;
            state.advancedSearch.params = action.payload;
            state.basicSearch.active = false;
            state.fileSearch.active = false;
        },

        setFileSearchParams: (state, action: PayloadAction<InventoryFileSearchParams>) => {
            state.currentPage = 0;
            state.fileSearch.active = true;
            state.fileSearch.params = action.payload;
            state.basicSearch.active = false;
            state.advancedSearch.active = false;
        },

        setSearchType: (state, action: PayloadAction<SearchTab>) => {
            state.searchType = action.payload;
        },

        setColumnDef: (state, action: PayloadAction<GridColumnDef[]>) => {
            state.columnDef = action.payload;
        },

        setColumnVisibility: (state, action: PayloadAction<GridColumnVisibilityMap>) => {
            state.columnVisibility = action.payload;
        }
    },
})

export const {
    setInventoryData,
    setInventoryDataRetrievedTS,
    setCurrentPage,
    setPageSize,
    setFilterModel,
    setSelectionModel,
    setSortModel,
    setBasicSearchParams,
    setBasicSearchParamsCsv,
    setAdvancedSearchParams,
    setFileSearchParams,
    setSearchType,
    setColumnDef,
    setColumnVisibility,
} = inventorySlice.actions

export default inventorySlice.reducer
